import React, { useState, useEffect, useLayoutEffect } from 'react';
import moment from 'moment';
import numeral from 'numeral';
import { useSelector } from 'react-redux';
import { CellItem, CellItemTotal } from 'src/components/CellItem';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  makeStyles,
  Button,
  IconButton,
  Collapse,
  Link,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
  Divider,
  Popper,
  Paper,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import { ViewLogs, useConfirmDialog } from 'src/components';
import { useRequest, useNotif, usePopup } from 'src/hooks';
import { Timezone } from 'src/constants';
import ReconDialog, {
  ReconUI,
  useReconActions,
  getUsernameByID
} from './Recon';

import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import OpenInNewIcon from '@material-ui/icons/Launch';
import EditIcon from '@material-ui/icons/Edit';
import SettingsIcon from '@material-ui/icons/Settings';
import ClearIcon from '@material-ui/icons/Clear';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';

import ReqBalDetails from '../JCdTrans/JournalEntry/ReqBalDetails';
import UpdateJEVDetails from './UpdateJEVDetails';
import { ACCOUNT_TYPES, TAX_SETTINGS, ACCOUNT_STYPE } from 'src/constants';
import { useToggle } from 'src/hooks';
import { sortBy } from 'lodash';
const {
  ASSETS,
  REVENUES,
  LIABILITIES,
  EXPENSES,
  OTHER_EXPENSES,
  OTHER_INCOME
} = ACCOUNT_TYPES;
const { ON_DEBIT } = TAX_SETTINGS;
const { INVENTORY, PPE, CHECKS_ON_HAND, CIB_CHECKING_ACC } = ACCOUNT_STYPE;

const useStyles = makeStyles(theme => ({
  root: {},
  item: {
    display: 'flex',
    flexDirection: 'column'
  },
  otherDetailsItem: {
    '&:not(:last-child)': {
      marginBottom: '1em'
    },

    listStyleType: 'none',
    paddingLeft: '1em '
  },
  otherDetailsValue: {
    fontWeight: '500',
    marginLeft: '.5em '
  },
  rowCursor: {
    cursor: 'pointer'
  },
  headerMargin: {
    '&:not(:first-child)': {
      marginTop: '1.5em'
    },

    fontSize: '.8rem',
    fontWeight: '500'
  },
  ulMargin: {
    marginTop: '1em'
  },
  showMorePadding: {
    padding: '.5em'
  },
  borderBottom: {
    '& > *': {
      borderBottom: 'unset'
    },
    cursor: 'pointer'
  },
  jevDetails: {
    display: 'flex',
    gap: '.5em'
  },
  row: {
    '&:hover': {
      '& $editButton': {
        display: 'block'
      }
    }
  },
  editButton: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block'
    },
    [theme.breakpoints.down('xs')]: {
      display: 'block'
    },
    [theme.breakpoints.down('sm')]: {
      display: 'block'
    }
  }
}));

const vatTypes = {
  0: 'Goods Other than Capital Goods',
  1: 'Services',
  2: 'Capital Goods'
};

const FLD_WIDTH = {
  sAccTitle: 400,
  acc_type: 30,
  rcRefNo: 100,
  AccNo: 100,
  Dr: 150,
  Cr: 150,
  Remarks: 200
};

const Grps = ({
  selected = 'normal',
  grps = [],
  lstSelected = [],
  multiple = false,
  onSelectGrp = () => null,
  caption = 'Group',
  defaultValueCaption = 'normal'
}) => {
  const handleClicItem = item => {
    onSelectGrp(item);
  };

  return (
    <Box display="flex" flexWrap="wrap" gridGap=".5em" alignItems="center">
      <Typography variant="body2" color="textSecondary" display="block">
        {caption}{' '}
      </Typography>
      {!multiple && (
        <Chip
          key={'normal'}
          variant={selected === 'normal' ? 'default' : 'outlined'}
          size="small"
          label={defaultValueCaption}
          color="primary"
          onClick={() => handleClicItem('normal')}
        />
      )}
      {grps.map(item => (
        <Chip
          key={item}
          variant={
            selected === item || lstSelected.includes(item)
              ? 'default'
              : 'outlined'
          }
          size="small"
          label={item}
          color="primary"
          onClick={() => handleClicItem(item)}
        />
      ))}
    </Box>
  );
};

const Row = ({
  caption,
  data,
  split = false,
  chip = false,
  align = 'left'
}) => {
  return (
    <Box display="flex" gridGap="2em" alignItems="center">
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ fontWeight: 600, flex: '0 0 10em' }}
      >
        {caption} :
      </Typography>
      {chip && !split && <Chip label={data} color="primary" size="small" />}
      {chip &&
        split &&
        data
          .split(',')
          .map(tag => (
            <Chip key={tag} label={tag} color="primary" size="small" />
          ))}
      {!chip && (
        <Typography
          variant="body2"
          align={align}
          style={{
            flex: 1
          }}
        >
          {data}
        </Typography>
      )}
    </Box>
  );
};

const Subsidiaries = ({ data }) => {
  const subsidiaries = [1, 2, 3, 4, 5]
    .map(sub => {
      const prop = {};

      prop['ixSubType'] = data?.['ixSubType' + sub] ?? 0;
      prop['sSubType'] = data?.['sSubType' + sub] ?? '';
      prop['sSubAcc'] = data?.['sSubAcc' + sub] ?? '';

      return prop;
    })
    .filter(item => item.ixSubType > 0);

  if (subsidiaries.length === 0) return null;

  return (
    <Box display="flex" flexDirection="column" gridGap="1em">
      {subsidiaries.map(sub => (
        <Row
          caption={sub[`sSubType`]}
          data={sub[`sSubAcc`]}
          key={sub.ixSubType}
        />
      ))}
    </Box>
  );
};

const ReconDetails = ({ localRecon }) => {
  // Only show if actually reconned (status > 0) AND has reflect date
  const hasReconDetails =
    localRecon?.recon_status > 0 &&
    localRecon?.recon_reflectDate &&
    localRecon?.recon_reflectDate !== '';

  if (!hasReconDetails) return null;

  return (
    <Box>
      <Box mb={2} display="flex" gridGap="1em">
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          Recon
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="1em" ml="1em">
        <Row
          caption="Recon Date"
          data={moment(localRecon?.recon_reflectDate).format('MM/DD/YYYY')}
        />
      </Box>
    </Box>
  );
};

const CheckDetails = ({ data }) => {
  const hasCheckDetails =
    (data?.check?.no || '') !== '' && data?.check?.date !== null;

  if (![CHECKS_ON_HAND, CIB_CHECKING_ACC].includes(data.SType)) return null;

  if (!hasCheckDetails) return null;

  return (
    <Box>
      <Box mb={2} display="flex" gridGap="1em">
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          Check Details
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="1em" ml="1em">
        {data.SType === CHECKS_ON_HAND && (
          <Row caption="Account No" data={data?.check?.accNo || ''} />
        )}
        <Row caption="Check No" data={data?.check?.no || ''} />
        <Row
          caption="Check Date"
          data={moment(data?.check?.date).format('MM/DD/YYYY')}
        />
      </Box>
    </Box>
  );
};

const RequireBalance = ({ data }) => {
  const [showReqBalDetails, setShowReqBalDetails] = useState(false);

  const { reqBalRefNo, reqBalDate, ixReqBalSrc } = data;

  const showRequireBalance =
    data && ixReqBalSrc !== 0 && ixReqBalSrc !== data?.ixTrans;

  if (!showRequireBalance) return null;

  return (
    <Box>
      <Box mb={2} display="flex" gridGap="1em">
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          Require Balance
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="1em" ml="1em">
        <Row caption="Source ID" data={ixReqBalSrc} />
        <Row caption="Reference No." data={reqBalRefNo} />
        <Row caption="Date" data={reqBalDate} />
        <Box>
          <Button
            size="small"
            color="primary"
            onClick={() => setShowReqBalDetails(true)}
          >
            More Details
          </Button>
        </Box>
      </Box>

      <ReqBalDetails
        ixReqBalSrc={ixReqBalSrc}
        open={showReqBalDetails}
        handleClose={() => setShowReqBalDetails(prev => !prev)}
      />
    </Box>
  );
};

const TaxDetails = ({ data, templateSettings }) => {
  const { tax: taxSettings } = useSelector(({ bizMeta }) => bizMeta);

  const showName = () => {
    for (let i = 1; i <= 5; i++) {
      const subAcc = data[`ixSub${i}`];

      if (subAcc === data?.tax?.ixSub) return false;
    }
    return true;
  };

  const getAmtType = () => {
    if (data.Dr > 0) return 1;
    else if (data.Cr > 0) return 2;
    else return 0;
  };

  const showWT = () => {
    return data?.tax_reqWT === getAmtType();
  };

  const showVAT = () => {
    if (data?.tax_reqVAT === getAmtType()) return true;

    if (data?.acc?.reqVATDeferred === getAmtType()) return true;

    return false;
  };

  const subType = () => {
    const accountType = data.acc.ixAccType;
    const jeTemplate = templateSettings?.je || [];
    const acc = jeTemplate.find(item => item.ixAcc === data.acc.ixAcc);

    if (acc?.tax?.sub_type === 'suppliers') return 'suppliers';
    else if (acc?.tax?.sub_type === 'customers') return 'customers';
    else if (data?.acc?.tax_subtype === 'suppliers') return 'suppliers';
    else if (data?.acc?.tax_subtype === 'customers') return 'customers';

    if (
      accountType === REVENUES &&
      (data?.acc?.reqVAT === ON_DEBIT || data?.acc?.reqWT === ON_DEBIT)
    )
      return 'suppliers';

    if (accountType === ASSETS && data.SType === INVENTORY) return 'suppliers';

    if (
      accountType === ASSETS &&
      data?.SType === PPE &&
      (data?.acc?.reqVAT === ON_DEBIT || data?.acc?.reqWT === ON_DEBIT)
    ) {
      return 'suppliers';
    }

    if (accountType === REVENUES || accountType === OTHER_INCOME)
      return 'customers';

    if (accountType === ASSETS) return 'customers';

    if (accountType === EXPENSES || accountType === OTHER_EXPENSES)
      return 'suppliers';

    if (accountType === LIABILITIES) return 'suppliers';

    return '';
  };

  const hasData = Boolean(data);
  const hasTaxSettings = taxSettings?.vat?.allow || taxSettings?.wt?.allow;
  const hasSub = (data?.tax?.ixSub || 0) !== 0;

  if (!hasData || !hasTaxSettings || !hasSub) return null;

  return (
    <Box>
      <Box mb={2} display="flex" gridGap="1em">
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          Tax Details
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="1em" ml="1em">
        {showName() && <Row caption="Name" data={data?.tax?.Name} />}
        <Row caption="TIN" data={data?.tax?.TIN} />
        <Row caption="O.R No" data={data?.tax?.RefNo} />
        <Row
          caption="Transaction Date"
          data={
            data?.tax?.RefDate
              ? moment(data?.tax?.RefDate).format('MM/DD/YYYY')
              : null
          }
        />
        {data?.tax?.ReportDate && (
          <Row
            caption="Report Date"
            data={
              data?.tax?.ReportDate
                ? moment(data?.tax?.ReportDate).format('MM/DD/YYYY')
                : null
            }
          />
        )}

        {showVAT() && subType() === 'suppliers' && (
          <Row
            caption="Purchase Type"
            data={vatTypes?.[data?.tax?.vatINtype]}
          />
        )}

        <Row
          caption="Gross"
          data={numeral(data?.tax?.tax_gross || 0).format('0,0.00')}
          align="right"
        />

        {showVAT() && (
          <Row
            caption="Vatable"
            data={numeral(data?.tax?.tax_vatable || 0).format('0,0.00')}
            align="right"
          />
        )}

        {showVAT() && subType() === 'suppliers' && (
          <Row
            caption="Non-VAT"
            data={numeral(data?.tax?.tax_nonvat || 0).format('0,0.00')}
            align="right"
          />
        )}

        {showVAT() && (
          <Row
            caption="Exempt"
            data={numeral(data?.tax?.tax_exempt || 0).format('0,0.00')}
            align="right"
          />
        )}

        {showVAT() && (
          <Row
            caption="Zero-Rated"
            data={numeral(data?.tax?.tax_zerorated || 0).format('0,0.00')}
            align="right"
          />
        )}

        {showVAT() && (
          <Row
            caption="VAT"
            data={`${numeral(
              data?.tax?.vatIN > 0 ? data?.tax?.vatIN : data?.tax?.vatOUT
            ).format('0,0.00')}`}
            align="right"
          />
        )}

        {showWT() && (
          <>
            <Row
              caption={`IT Withheld - ${data?.tax?.wtATC}`}
              data={numeral(
                data?.tax?.wtAmtIN > 0
                  ? data?.tax?.wtAmtIN
                  : data?.tax?.wtAmtOUT
              ).format('0,0.00')}
              align="right"
            />

            {subType() === 'customers' && (
              <>
                <Row
                  caption={`VAT/PT Withheld - ${data?.tax?.vatATC}`}
                  data={numeral(data?.tax?.vatWTamt).format('0,0.00')}
                  align="right"
                />
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const Grp = ({ data }) => {
  return (
    <Box display="flex" flexDirection="column" gridGap="1em" ml="1em">
      <Row caption="Group" data={data?.grp || ''} chip />
    </Box>
  );
};

const Tags = ({ data }) => {
  return (
    <Box display="flex" flexDirection="column" gridGap="1em" ml="1em">
      <Row caption="Tags" data={data?.tag || ''} chip split />
    </Box>
  );
};

const Settings = ({
  anchorEl,
  onCloseSettings,
  grpShown,
  tagShown,
  sort,
  brchFilterShown,
  brchColShown,
  zeroItemsShown,
  toggleShowBrchFilter,
  toggleShowBrchCol,
  toggleShowTag,
  toggleShowGrpItem,
  toggleShowZeroItems,
  toggleSortJE
}) => {
  const { brch } = useSelector(({ bizMeta }) => bizMeta);
  return (
    <Popper
      id={Boolean(anchorEl) ? 'je-settings' : undefined}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-end"
    >
      <Box
        component={Paper}
        display="flex"
        flexDirection="column"
        gridGap="1.5em"
        p={2}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body1" color="textSecondary">
            Settings
          </Typography>
          <IconButton size="small" onClick={onCloseSettings}>
            <ClearIcon fontSize="inherit" />
          </IconButton>
        </Box>

        <FormControlLabel
          control={
            <Switch size="small" checked={tagShown} onChange={toggleShowTag} />
          }
          labelPlacement="end"
          label="Show Tag Filter"
        />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={grpShown}
              onChange={toggleShowGrpItem}
            />
          }
          labelPlacement="end"
          label="Show Group Filter"
        />

        {brch?.mode > 0 && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={brchFilterShown}
                onChange={toggleShowBrchFilter}
              />
            }
            labelPlacement="end"
            label="Show Branch Filter"
          />
        )}

        {brch?.mode > 0 && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={brchColShown}
                onChange={toggleShowBrchCol}
              />
            }
            labelPlacement="end"
            label="Show Branch Column"
          />
        )}

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={zeroItemsShown}
              onChange={toggleShowZeroItems}
            />
          }
          labelPlacement="end"
          label="Show Zero Items"
        />

        <FormControlLabel
          control={
            <Switch size="small" checked={sort} onChange={toggleSortJE} />
          }
          labelPlacement="end"
          label="Sort Entries"
        />
      </Box>
    </Popper>
  );
};

const Item = ({
  items,
  jv,
  setUpdateRemarks,
  columns,
  grpShown,
  setJV,
  ...rest
}) => {
  const classes = useStyles();
  const { tax: taxSettings } = useSelector(({ bizMeta }) => bizMeta);

  const [showOtherDetails, setShowOtherDetails] = useState(false);
  const [reconModalOpen, setReconModalOpen] = useState(false);

  const { users } = useSelector(({ bizMeta }) => bizMeta);
  const { current_user } = useSelector(state => state.auth);
  const request = useRequest();
  const notify = useNotif();
  const { prompt } = useConfirmDialog();
  const { popup } = usePopup();

  const [showReqBalDetails, setShowReqBalDetails] = useState(false);
  const [checkReqbalSrc, setCheckReqbalSrc] = useState({});

  const handleOpenReconModal = e => {
    e.stopPropagation();
    setReconModalOpen(true);
  };

  const handleCloseReconModal = () => setReconModalOpen(false);

  const reconTrans = {
    id: items.ixTrans,
    acc_id: items?.acc?.ixAcc || 0,

    recon_status: items?.recon_status ?? items?.recon?.status ?? 0,
    recon_reflectDate:
      items?.recon_reflectDate ?? items?.recon?.reflectDate ?? '',
    recon_remarks: items?.recon_remarks ?? items?.recon?.remarks ?? '',
    recon_ixUser: items?.recon_ixUser ?? items?.recon?.ixUser ?? 0,
    recon_date: items?.recon_date ?? items?.recon?.recon_date ?? '',
    jDate: jv?.jDate
      ? moment(jv.jDate).format('YYYY-MM-DDTHH:mm:ss')
      : moment().format('YYYY-MM-DDTHH:mm:ss'),

    // Prefer cleared date; if none, fall back to JV date
    CheckDateCleared: items.CheckDateCleared || jv?.jDate || null
  };

  // Local copy para sa Recon UI lang, para sure na agad nagre-reflect yung
  // checkbox at reflect date kahit may ibang logic pa sa JE list.
  const [localRecon, setLocalRecon] = useState(reconTrans);

  useEffect(() => {
    setLocalRecon(reconTrans);
  }, [
    items.ixTrans,
    items.recon_status,
    items.recon_reflectDate,
    items.recon_remarks,
    items.recon_ixUser,
    items.recon_date,
    jv?.jDate
  ]);

  // Use shared recon actions from Recon.js
  const reconActions = useReconActions({
    prompt,
    current_user,
    setJournalEntries: reconData => {
      setJV(prev => {
        const updated = { ...prev };
        const jeItems = updated?.trans_je?.items || [];
        const idx = jeItems.findIndex(item => item.ixTrans === items.ixTrans);
        if (idx !== -1) {
          const newItems = [...jeItems];
          newItems[idx] = {
            ...newItems[idx],
            recon_status: reconData.recon_status,
            status: reconData.recon_status,
            recon_ixUser: reconData.recon_ixUser,
            recon_date: reconData.recon_date,
            recon_reflectDate: reconData.recon_reflectDate,
            recon_remarks: reconData.recon_remarks
          };
          updated.trans_je = { ...updated.trans_je, items: newItems };
        }
        // sabay i-update ang localRecon para sa modal view
        setLocalRecon(prev => ({ ...prev, ...reconData }));
        return updated;
      });
    },
    notify,
    request,
    Timezone,
    users,
    popup
  });

  const allowRecon = Boolean(items.recon);

  const hasSubsidiaries = () => {
    let found = false;

    for (let i = 1; i <= 5; i++) {
      const subAccId = items[`ixSubType${i}`];

      if (subAccId === 0 || subAccId === null) {
        continue;
      }

      found = true;
      break;
    }

    return found;
  };

  const hasCheckDetails = () =>
    [CHECKS_ON_HAND, CIB_CHECKING_ACC].includes(items.SType);

  const hasReqBal = () => {
    const { ixReqBalSrc } = items;

    return ixReqBalSrc !== 0 && ixReqBalSrc !== items?.ixTrans;
  };

  const hasTaxDetails = () => {
    const getAmtType = () => {
      if (items.Dr > 0) return 1;
      else if (items.Cr > 0) return 2;
      else return 0;
    };

    const showWT = () => {
      return items?.tax_reqWT === getAmtType();
    };

    const showVAT = () => {
      if (items?.tax_reqVAT === getAmtType()) return true;

      if (items?.acc?.reqVATDeferred === getAmtType()) return true;

      return false;
    };

    const hasTaxSettings =
      Boolean(taxSettings?.vat?.allow) || Boolean(taxSettings?.wt?.allow);
    const hasSub = items?.tax?.ixSub !== 0;
    const viewVATandWT = showVAT() || showWT();

    return hasTaxSettings && hasSub && viewVATandWT;
  };

  const handleOpen = e => {
    e.stopPropagation();

    setShowOtherDetails(prev => !prev);
  };

  const getLink = () => {
    const accTitle = items?.acc?.sAcc?.replaceAll('/', '_');
    const timeStart = 'T00:00:00';
    const timeEnd = 'T23:59:59';
    const date = moment(jv?.jDate);
    const ixBrch = jv?.ixBrch || 0;
    const start = date.startOf('month').format('YYYY-MM-DD') + timeStart;
    const end = date.endOf('month').format('YYYY-MM-DD') + timeEnd;
    const sDate =
      'For the month ended ' + date.endOf('month').format('MMMM, DD YYYY');

    return `/app/reports/gl/id/${items?.acc?.ixAcc}/${accTitle}/start/${start}/end/${end}/sDate/${sDate}/ixbrch/${ixBrch}/action/submit`;
  };

  const updateJERemarks = (value, ixTrans, sAccTitle) => {
    setUpdateRemarks(prev => ({
      ...prev,
      open: true,
      jid: jv.jid,
      type: 'je',
      ixTrans,
      value,
      title: 'Update JE Remarks',
      subTitle: sAccTitle
    }));
  };

  const handleCheckRecBal = item => {
    setCheckReqbalSrc(item);
    setShowReqBalDetails(true);
  };

  return (
    <>
      <TableRow
        className={`${classes.borderBottom} ${classes.row}`}
        onClick={handleOpen}
      >
        <TableCell padding="checkbox" align="center">
          <IconButton aria-label="expand row" size="small" onClick={handleOpen}>
            {showOtherDetails ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        </TableCell>
        {columns.map((fld, index) => {
          return (
            <CellItem
              key={fld.fld}
              items={items}
              fld={fld.fld === 'remarks' ? { ...fld, fld: 'Part' } : fld}
              hideZero={true}
              indent={items.Cr !== 0 && index === 1}
              width={FLD_WIDTH?.[fld.fld] ?? 'auto'}
              maxWidth={FLD_WIDTH?.[fld.fld] ?? 'auto'}
              toolTip={fld?.toolTip ?? ''}
              handleCheckRecBal={handleCheckRecBal}
            />
          );
        })}
        <ReqBalDetails
          ixReqBalSrc={checkReqbalSrc?.ixReqBalSrc}
          open={showReqBalDetails}
          handleClose={() => setShowReqBalDetails(prev => !prev)}
        />
        <TableCell padding="checkbox" align="right">
          <Tooltip title="Update Remarks">
            <IconButton
              onClick={() =>
                updateJERemarks(items.Remarks, items.ixTrans, items.sAccTitle)
              }
              className={classes.editButton}
              size="small"
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} />
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan="100%">
          <Collapse in={showOtherDetails} timeout="auto" unmountOnExit>
            <Box display="flex" justifyContent="right" gridGap={8}>
              {allowRecon && (
                <Tooltip title="Recon Tool">
                  <IconButton size="small" onClick={handleOpenReconModal}>
                    {localRecon.recon_status ? (
                      <CheckCircleIcon fontSize="small" color="primary" />
                    ) : (
                      <CheckCircleOutlineIcon
                        fontSize="small"
                        color="disabled"
                      />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <Button
                component={Link}
                color="primary"
                variant="text"
                tabIndex={-1}
                href={getLink()}
                target="_blank"
                endIcon={<OpenInNewIcon fontSize="small" />}
                size="small"
              >
                View General Ledger
              </Button>
            </Box>
            <Box display="flex" mb={3} mt={1}>
              <Box display="flex" flexDirection="column" gridGap="3em" flex={1}>
                {hasSubsidiaries() && <Subsidiaries data={items} />}
                {hasTaxDetails() && (
                  <TaxDetails
                    data={items}
                    templateSettings={rest.templateSettings}
                  />
                )}
              </Box>
              <Box display="flex" flexDirection="column" gridGap="3em" flex={1}>
                {(items?.tag ?? '') !== '' && <Tags data={items} />}
                {items.grp !== '' && grpShown && <Grp data={items} />}
                {hasReqBal() && <RequireBalance data={items} />}
                <ReconDetails localRecon={localRecon} />
                {hasCheckDetails() && <CheckDetails data={items} />}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <Dialog
        open={reconModalOpen}
        onClose={handleCloseReconModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h3">Reconciliation</Typography>
        </DialogTitle>
        <DialogContent>
          <ReconUI
            trans={localRecon}
            handleReconToggle={reconActions.handleReconToggle}
            viewLogs={reconActions.viewLogs}
            handleShowReconDetails={reconActions.handleShowReconDetails}
          />

          <ReconDialog
            open={reconActions.reconDetailsOpen}
            onClose={reconActions.closeReconDetails}
            onUpdate={reconActions.handleReconDialogUpdate}
            reconDetails={reconActions.reconDetails}
            onReconDetailsChange={reconActions.onReconDetailsChange}
            getUsernameByID={ixUser => getUsernameByID(ixUser, users)}
            reconUpdateOpen={reconActions.reconUpdateOpen}
            openReconUpdate={reconActions.openReconUpdate}
            closeReconUpdate={reconActions.closeReconUpdate}
          />

          <ViewLogs
            url={'/trans/recon/logs'}
            title="Recon Logs"
            method="POST"
            payload={{
              ixAcc: reconTrans.acc_id,
              id: reconTrans.id
            }}
            openTransactionDialog={reconActions.logsOpen}
            setOpenTransactionDialog={reconActions.setLogsOpen}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReconModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const JVTransJE = ({ className, jv, setJV, setUpdateRemarks, ...rest }) => {
  const classes = useStyles();
  const { JEVno, brch } = useSelector(({ bizMeta }) => bizMeta);

  const [updateJEVDetailsShown, showForm, hideForm] = useToggle();
  const [zeroItemsShown, showZeroItem, hideZeroItem] = useToggle(true);
  const [grpShown, showGrp, hideGrp] = useToggle(false);
  const [tagShown, showTag, hideTag] = useToggle(false);
  const [brchColShown, showBrchCol, hideBrchCol] = useToggle(true);
  const [brchFilterShown, showBrchFilter, hideBrchFilter] = useToggle(false);
  const [anchorElement, setAnchorElement] = useState(null);
  const [sort, setSort] = useState(false);

  const [tags, setTags] = useState([]);
  const [grp, setGrp] = useState('normal');
  const [brchGrp, setBrchGrp] = useState('normal');

  const canEditJEVDetails =
    jv?._rm?.priv?.['entry.edit'] || jv?._rm?.priv?.['entry.locked.edit'];
  const canEditRemarks =
    jv?._rm?.priv?.['entry.edit'] || jv?._rm?.priv?.['entry.edit.remarks-only'];
  const checkSked = jv?.trans_je?.check_sked?.items || [];
  const jeItems = jv?.trans_je?.items || [];
  const itemsToUse = checkSked.length !== 0 ? checkSked : jeItems;
  const reqJEVno =
    JEVno?.require &&
    jv?._rm?.showJE &&
    (JEVno?.ixJCd ?? []).includes(jv?.ixJCd ?? 0);
  const isJEVRefNoCode = jv?._rm?.refno?.code === 'JEV';

  const grpLst = itemsToUse.reduce((acc, prev) => {
    if ((prev?.grp ?? '') !== '' && !acc.includes(prev.grp)) {
      acc.push(prev.grp);
      return acc;
    }
    return acc;
  }, []);

  const brchGrpLst = itemsToUse.reduce((acc, prev) => {
    if (Boolean(prev.sBrch) && !acc.includes(prev.sBrch)) {
      acc.push(prev.sBrch);
      return acc;
    }
    return acc;
  }, []);

  const tagLst = itemsToUse.reduce((acc, prev) => {
    const { tag } = prev;

    if (tag === '' || tag === null) return acc;

    tag.split(',').forEach(item => {
      if (!acc.includes(item)) acc.push(item);
    });

    return acc;
  }, []);

  const journalEntries = sortBy(
    itemsToUse.map((items, index) => {
      const data = { ...items, index };
      data.Qty = data.crQty + data.drQty;
      data.Rate = data.crRate + data.drRate;
      data.rateAndQtyTotal = data.Qty * data.Rate;
      return data;
    }),
    [sort ? 'index' : 'ixTrans']
  )
    .filter(data => (zeroItemsShown ? true : data.Cr !== 0 || data.Dr !== 0))
    .filter(item =>
      brchGrpLst.length && brchGrp !== 'normal' ? item.sBrch === brchGrp : true
    )
    .filter(item =>
      grpLst.length && grp !== 'normal' ? item.grp === grp : true
    )
    .filter(item => {
      if (tagLst.length && tags.length) {
        const itemTags = item.tag === '' ? [] : item.tag.split(',');
        return itemTags.some(tg => tags.includes(tg));
      } else return true;
    });

  const showRC = jv?.trans_je?.items?.some(item => item.acc.ixExpCls > 0);
  const qtyCaption = jv?._rm?.webEntry?.trans_je?.qtyCaption || 'Qty';
  const rateCaption = jv?._rm?.webEntry?.trans_je?.rateCaption || 'Rate';
  const qtyAndRateTotalCaption =
    jv?._rm?.webEntry?.trans_je?.qtyAndRateTotalCaption || 'Amount';
  const showQty = jv?._rm?.webEntry?.trans_je?.showQty ?? true;
  const showRate = jv?._rm?.webEntry?.trans_je?.showRate ?? true;
  const showQtyAndRateTotal =
    jv?._rm?.webEntry?.trans_je?.showQtyAndRateTotal ?? true;

  const getColumns = () => {
    const je = itemsToUse;
    const [acc, ...rest] = jv._rm.trans_je.flds;
    const columns = [...rest];
    let leftColumns = [],
      showQtyAndRate = false,
      showBrch = false;

    for (let i = 0; i < je.length; i++) {
      const item = je[i];
      if (item?.acc?.reqQtyRate > 0) {
        showQtyAndRate = true;
        break;
      }
    }

    const branches = [];
    for (let i = 0; i < je.length; i++) {
      const item = je[i];

      if (item.ixBrch > 0 && !branches.includes(item.ixBrch)) {
        branches.push(item.ixBrch);
      }

      if (branches.length > 1) {
        showBrch = true;
        break;
      }
    }

    if (showBrch && brchColShown) {
      leftColumns.push({
        caption: brch?.label ?? 'Branches',
        fld: 'sBrch',
        align: 'center',
        totalCaption: ''
      });
    }

    if (showRC) {
      leftColumns.push({
        caption: 'RC',
        fld: 'rcRefNo',
        align: 'center',
        toolTip: 'sRC',
        totalCaption: ''
      });
    }

    leftColumns.push({
      caption: '',
      fld: 'acc_type',
      totalCaption: ''
    });

    if (jv?._rm?.webEntry?.trans_je?.showAccNoCol) {
      columns.unshift({
        caption: 'Acc Code',
        fld: 'AccNo',
        align: 'center',
        totalCaption: ''
      });
    }

    if (showQtyAndRate && showQtyAndRateTotal) {
      columns.unshift({
        caption: qtyAndRateTotalCaption,
        fld: 'rateAndQtyTotal',
        totalCaption: '',
        numeral: '0,0.00',
        total: true
      });
    }

    if (showQtyAndRate && showRate) {
      columns.unshift({
        caption: rateCaption,
        fld: 'Rate',
        totalCaption: '',
        numeral: '0,0.00'
      });
    }

    if (showQtyAndRate && showQty) {
      columns.unshift({
        caption: qtyCaption,
        fld: 'Qty',
        totalCaption: '',
        numeral: '0,0.00'
      });
    }

    return [...leftColumns, acc, ...columns];
  };

  const getTotals = () => {
    let totals = { ...(jv?.trans_je?.total || {}) };
    const checkSkedTotals = { ...(jv?.trans_je?.check_sked?.total || {}) };

    if (checkSked.length !== 0) totals = checkSkedTotals;

    totals.rateAndQtyTotal = journalEntries.reduce(
      (acc, item) => acc + item.rateAndQtyTotal,
      0
    );

    // group totals
    if (grp !== 'normal' || brchGrp !== 'normal' || tags.length > 0) {
      return Object.entries(totals).reduce((acc, [key, _]) => {
        if (!Boolean(acc?.[key])) {
          return {
            ...acc,
            [key]: journalEntries.reduce((t, p) => t + p[key], 0)
          };
        }
      }, {});
    } else return totals;
  };

  const handleSelectGrpItem = item => {
    setGrp(item);
  };

  const handleSelectBrchGrp = item => {
    setBrchGrp(item);
  };

  const handleSelectTag = item => {
    setTags(prev => {
      if (prev.includes(item)) return prev.filter(tag => item !== tag);
      return [...prev, item];
    });
  };

  const toggleShowZeroItems = e => {
    if (e.target.checked) showZeroItem();
    else hideZeroItem();
  };

  const toggleShowGrpItem = e => {
    if (e.target.checked) showGrp();
    else hideGrp();
  };

  const toggleShowTag = e => {
    if (e.target.checked) showTag();
    else hideTag();
  };

  const toggleShowBrchFilter = e => {
    if (e.target.checked) showBrchFilter();
    else hideBrchFilter();
  };

  const toggleShowBrchCol = e => {
    if (e.target.checked) showBrchCol();
    else hideBrchCol();
  };

  const toggleSortJE = e => {
    setSort(e.target.checked);
  };

  const showSettings = e => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorElement(e.currentTarget);
  };

  if (!jv._rm.showJE) return null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" p={2}>
        <Box flex={1}>
          {reqJEVno && !isJEVRefNoCode && (
            <>
              <Typography variant="caption" display="block">
                JEV # | JEV Date
              </Typography>
              <Tooltip title="Update JEV details">
                <Link
                  component="button"
                  variant="h4"
                  color="textSecondary"
                  underline="none"
                  onClick={() => {
                    if (!canEditJEVDetails) return;
                    showForm();
                  }}
                  className={`${canEditJEVDetails ? classes.row : ''} ${
                    classes.jevDetails
                  }`}
                >
                  <span>
                    {jv?.JEVno === '' ? '-' : jv?.JEVno} |{' '}
                    {moment(jv?.jDate).format('MM/DD/YYYY')}
                  </span>
                  <span className={classes.editButton}>
                    <EditIcon fontSize="inherit" />
                  </span>
                </Link>
              </Tooltip>
              <UpdateJEVDetails
                open={updateJEVDetailsShown}
                onClose={hideForm}
                jv={jv}
                setJV={setJV}
              />
            </>
          )}
        </Box>
        <Box display="flex" justifyContent="end" flex={1}>
          <ClickAwayListener
            onClickAway={() => {
              setAnchorElement(null);
            }}
          >
            <Box>
              <Tooltip title="Settings">
                <IconButton
                  onClick={showSettings}
                  aria-describedby={
                    Boolean(anchorElement) ? 'je-settings' : undefined
                  }
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Settings
                anchorEl={anchorElement}
                onCloseSettings={() => {
                  setAnchorElement(null);
                }}
                tagShown={tagShown}
                grpShown={grpShown}
                sort={sort}
                brchFilterShown={brchFilterShown}
                brchColShown={brchColShown}
                zeroItemsShown={zeroItemsShown}
                toggleShowBrchFilter={toggleShowBrchFilter}
                toggleShowBrchCol={toggleShowBrchCol}
                toggleShowTag={toggleShowTag}
                toggleShowGrpItem={toggleShowGrpItem}
                toggleShowZeroItems={toggleShowZeroItems}
                toggleSortJE={toggleSortJE}
              />
            </Box>
          </ClickAwayListener>
        </Box>
      </Box>

      {(grpShown || brchFilterShown || tagShown) && (
        <>
          <Divider />
          <Box pl={2} pt={2}>
            <Typography variant="h5" color="textSecondary">
              Filters
            </Typography>
          </Box>

          <Box display="flex" p={2} gridGap="2.5em">
            {grpShown && (
              <Grps
                selected={grp}
                grps={grpLst}
                onSelectGrp={handleSelectGrpItem}
              />
            )}
            {brchFilterShown && (
              <Grps
                selected={brchGrp}
                grps={brchGrpLst}
                onSelectGrp={handleSelectBrchGrp}
                caption={brch?.label_singular ?? 'Branch'}
                defaultValueCaption="all"
              />
            )}
            {tagShown && (
              <Grps
                lstSelected={tags}
                grps={tagLst}
                multiple
                onSelectGrp={handleSelectTag}
                caption="Tags"
              />
            )}
          </Box>
        </>
      )}

      <Divider />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              {getColumns().map((fld, index) => (
                <TableCell
                  key={fld.fld}
                  style={{
                    width: FLD_WIDTH[fld.fld],
                    minWidth: FLD_WIDTH[fld.fld]
                  }}
                  align="center"
                >
                  {fld.caption}
                </TableCell>
              ))}
              {canEditRemarks && (
                <TableCell padding="checkbox">
                  <IconButton />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {journalEntries.length === 0 && (
              <TableRow>
                <TableCell colSpan="100%" align="center">
                  {' '}
                  No data to show{' '}
                </TableCell>
              </TableRow>
            )}

            {journalEntries.map(item => (
              <Item
                items={item}
                jv={jv}
                key={item.ixTrans}
                columns={getColumns()}
                setUpdateRemarks={setUpdateRemarks}
                setJV={setJV}
                {...rest}
              />
            ))}

            {journalEntries.length !== 0 && (
              <TableRow>
                <TableCell padding="checkbox" />
                {getColumns().map((fld, index) => (
                  <CellItemTotal key={index} items={getTotals()} fld={fld} />
                ))}
                {canEditRemarks && (
                  <TableCell padding="checkbox">
                    <IconButton />
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default JVTransJE;
